import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import {
  fetchSignInPasswordState,
  saveSignInPassword,
} from "../../features/portal/slice-extensions/portal-password-slice-extension";
import { meetsPasswordRules } from "../../features/portal/utils/passwordRules";

type Loaded = { hasPassword: boolean } | null;

/** The line under the fields: the server's reason when it has one, else the form's own check. */
const errorKeyFor = (error: ApiErrorResponse | undefined): string => {
  if (error?.code === "wrong-password") return "portal.password.wrong";
  if (error?.code === "password-rules") return "portal.password.rule";
  return "portal.password.failed";
};

/**
 * The signed-in person's own sign-in password on Me (US-113, PD-0056): "Set a password" while the sign-in
 * has none, "Change password" (the current one first) once it has. Hidden for a person without an email of
 * their own (the endpoint answers 409) and while a ward is shown.
 */
const PortalSignInPasswordCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [state, setState] = useState<Loaded>(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchSignInPasswordState())
      .unwrap()
      .then(setState)
      .catch(() => setState(null));
  }, [dispatch]);

  if (!state) return null;
  const changing = state.hasPassword;

  const validate = (): string | null => {
    if (changing && current.length === 0) return "portal.password.currentMissing";
    if (!meetsPasswordRules(next)) return "portal.password.rule";
    if (next !== repeat) return "portal.password.mismatch";
    return null;
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const invalid = validate();
    setErrorKey(invalid);
    if (invalid) return;
    setBusy(true);
    const result = await dispatch(saveSignInPassword({ ...(changing ? { currentPassword: current } : {}), newPassword: next }));
    setBusy(false);
    if (saveSignInPassword.fulfilled.match(result)) {
      setCurrent("");
      setNext("");
      setRepeat("");
      setState({ hasPassword: true });
      toast.success(t("portal.password.saved"));
    } else {
      setErrorKey(errorKeyFor(result.payload));
    }
  };

  return (
    <form className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10" onSubmit={save} noValidate>
      <h2 className="flex items-center gap-2 text-base font-semibold text-grey-20 dark:text-white">
        <KeyRound size={20} />
        {t(changing ? "portal.password.changeTitle" : "portal.password.setTitle")}
      </h2>
      <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">{t("portal.password.intro")}</p>
      <div className="mt-3 space-y-3">
        {changing && (
          <label className="block text-sm font-medium text-grey-30 dark:text-grey-70">
            {t("portal.password.current")}
            <input
              type="password"
              className="input mt-1"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </label>
        )}
        <label className="block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("portal.password.new")}
          <input
            type="password"
            className="input mt-1"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
        </label>
        <label className="block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("portal.password.repeat")}
          <input
            type="password"
            className="input mt-1"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-grey-50 dark:text-grey-60">{t("portal.password.rule")}</p>
      {errorKey && (
        <p role="alert" className="field-error mt-3">
          {t(errorKey)}
        </p>
      )}
      <div className="mt-3 flex justify-end">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {t("common.save")}
        </button>
      </div>
    </form>
  );
};

export default PortalSignInPasswordCard;
